import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { Box, HStack, Skeleton, Text, VStack } from '@chakra-ui/react';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { CopyButton } from '@app/components/CopyButton';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { QuickAmountButtons } from '@app/components/Swap/components/QuickAmountButtons';
import { toOptionalFixed } from '@app/helpers/format';
import { useWithdrawLiquidity } from '@app/hooks/liquidity/useWithdrawLiquidity';
import { useWithdrawLiquidityOutput } from '@app/hooks/liquidity/useWithdrawLiquidityOutput';
import { WithdrawLiquiditySubmitButton } from '@app/screens/pair-details/WithdrawLiquiditySubmitButton';
import { Pair } from '@app/types/pool';
import fromExponential from 'from-exponential';
import { useTokenPriceValue } from '@app/hooks/token/useTokenPriceValue';

interface Props {
  pair: Pair;
  balance: number;
  getWithdrawBalance: () => Promise<number | void>;
  onClose?: () => void;
}

function shrinkDecimalValue(numString: string, precision: number): string {
  const num = parseFloat(numString);

  if (isNaN(num)) {
    return '';
  }

  // Use toFixed method to shrink the value to a given amount of decimals
  return fromExponential(parseFloat(num.toFixed(precision)));
}

export const WithdrawLiquidity: FC<Props> = ({
  pair,
  balance,
  getWithdrawBalance,
  onClose
}) => {
  const [amount, setAmountValue] = useState('');

  const {
    withdrawLiquidity,
    withdrawingLiquidity,
    allowance,
    pairDecimals,
    approveWithdraw,
    approvingWithdraw
  } = useWithdrawLiquidity(pair, amount);

  const setAmount = useCallback(
    (newValue: string) => {
      setAmountValue(shrinkDecimalValue(newValue, pairDecimals));
    },
    [pairDecimals]
  );

  const { output, loading } = useWithdrawLiquidityOutput(pair.id, amount, pair);

  const { value: priceValueToken0 } = useTokenPriceValue(
    output?.amount0?.toString() ?? '',
    pair.token0.id
  );
  const { value: priceValueToken1 } = useTokenPriceValue(
    output?.amount1?.toString() ?? '',
    pair.token1.id
  );

  const totalPriceUsd = useMemo(() => {
    if (priceValueToken0 === undefined || priceValueToken1 === undefined) {
      return;
    }

    return priceValueToken0 + priceValueToken1;
  }, [priceValueToken0, priceValueToken1]);

  function renderInfoRow(
    label: string,
    value: string | number,
    loading?: boolean,
    copyValue?: boolean,
    rawValue?: string
  ) {
    function renderContent() {
      if (loading) {
        return (
          <Skeleton
            height="16px"
            width="64px"
            startColor="neutral.700"
            endColor="neutral.800"
            mb="2px"
          />
        );
      }

      return copyValue && rawValue ? (
        <HStack>
          <Text>{value}</Text>
          <CopyButton text={rawValue} />
        </HStack>
      ) : (
        <Text>{value}</Text>
      );
    }

    return (
      <HStack justifyContent="space-between" mb={2} color="whiteOpacity.70">
        <Text fontWeight={400} fontSize="14px">
          {label}
        </Text>
        {renderContent()}
      </HStack>
    );
  }

  return (
    <>
      <VStack w="100%">
        <Box w="100%">
          <Text
            textAlign="left"
            w="100%"
            color="neutral.300"
            fontSize="12px"
            lineHeight="18px"
            letterSpacing="-0.18px"
            mb="4px"
          >
            Amount
          </Text>
          <DebouncedInput
            textAlign="left"
            amount={amount}
            onChange={val => {
              setAmount(fromExponential(val));
            }}
          />
          <HStack w="100%" justify="space-between">
            <HStack justify="flex-start">
              <Text
                color="neutral.500"
                fontSize="12px"
                fontWeight="400"
                lineHeight="18px"
                letterSpacing="-0.18px"
              >
                Balance:
              </Text>
              <Text>{fromExponential(balance)}</Text>
            </HStack>
          </HStack>
        </Box>
        <QuickAmountButtons
          amount={amount}
          balance={balance}
          onUpdate={val => setAmount(val)}
          onMax={() => setAmount(balance.toString())}
        />
        <Box
          borderTop="1px solid"
          borderColor="whiteOpacity.6"
          pt="12px"
          w="100%"
        >
          {renderInfoRow(
            `Total amount USD`,
            toOptionalFixed(Number(totalPriceUsd), 5)
          )}
          {renderInfoRow(
            `${pair.token0.symbol} amount`,
            output?.amount0 !== undefined
              ? toOptionalFixed(Number(output?.amount0), 5)
              : 'n/a',
            loading
          )}
          {renderInfoRow(
            `${pair.token1.symbol} amount`,
            output?.amount1 !== undefined
              ? toOptionalFixed(Number(output?.amount1), 5)
              : 'n/a',
            loading
          )}
        </Box>
        {+amount > balance && (
          <Alert
            status="error"
            display="flex"
            border="1px solid"
            borderColor="danger.800"
            mt="16px"
            borderRadius="10px"
            py="10px"
            px="12px"
          >
            <AlertIcon w="16px" h="16px" mr="8px" color="orange" />
            <AlertDescription color="danger.200">
              Available balance exceeded
            </AlertDescription>
          </Alert>
        )}
      </VStack>
      <WithdrawLiquiditySubmitButton
        allowance={allowance}
        amount={amount}
        balance={balance}
        isWithdrawInProgress={withdrawingLiquidity}
        isApproveInProgress={approvingWithdraw}
        withdrawLiquidity={withdrawLiquidity}
        approveWithdraw={approveWithdraw}
        onClose={() => {
          onClose?.();
        }}
        getWithdrawBalance={getWithdrawBalance}
      />
    </>
  );
};
